/**
 * Hour-by-hour timeline for Plan my day: Outlook meetings as fixed grey blocks, planned tasks at their start times.
 * Drop a task on a time (15-minute snap) to pin it there, drag a block to move it, pull its bottom edge to change its
 * minutes. Times are Malta wall-clock minutes from midnight of the planned day.
 */
import clsx from "clsx";
import { ChevronDown, ChevronUp, Pin } from "lucide-react";
import { useRef, useState } from "react";

export interface Pick { itemId: string; minutes: number; at?: string | null }
export type Slot = Pick & { start: number | null; pinned: boolean };
interface Meeting { subject: string; start: string; end: string; isAllDay: boolean; location: string | null }

export const DRAG_TYPE = "application/x-one-plan-task";
/** The task being dragged (dataTransfer can't be read during dragover). grab: pixels between the block top and the pointer. */
export const dragState: { current: { id: string; grab: number; minutes: number } | null } = { current: null };

const TZ = "Europe/Malta";
const PX = 48 / 60; // pixels per minute
const SNAP = 15;

/** Epoch ms of a Malta wall-clock time on a day. */
export function maltaInstant(day: string, hhmm = "00:00"): number {
  const guess = new Date(`${day}T${hhmm}:00Z`);
  const off = new Date(guess.toLocaleString("en-US", { timeZone: TZ })).getTime() - new Date(guess.toLocaleString("en-US", { timeZone: "UTC" })).getTime();
  return guess.getTime() - off;
}
export const minOf = (iso: string, zero: number) => Math.round((Date.parse(iso) - zero) / 60_000);
export const toMin = (hhmm: string) => { const [h, m] = hhmm.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
export const toHHMM = (min: number) => { const m = Math.max(0, Math.min(1439, Math.round(min))); return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; };
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

/** Side-by-side lanes for overlapping blocks. */
function lanes<T extends { s: number; e: number }>(bs: T[]) {
  const out = new Map<T, { lane: number; of: number }>();
  let cluster: T[] = [], ends: number[] = [], until = -1;
  const flush = () => { for (const b of cluster) out.get(b)!.of = ends.length; cluster = []; ends = []; };
  for (const b of [...bs].sort((x, y) => x.s - y.s || y.e - x.e)) {
    if (b.s >= until) { flush(); until = -1; }
    let lane = ends.findIndex((e) => e <= b.s);
    if (lane < 0) { lane = ends.length; ends.push(b.e); } else ends[lane] = b.e;
    out.set(b, { lane, of: 0 });
    cluster.push(b);
    until = Math.max(until, b.e);
  }
  flush();
  return out;
}

export function DayTimeline({ date, zero, workday, meetings, slots, titleOf, onPlace, onUnpin, onResize, onMove }: {
  date: string; zero: number; workday: { start: string; end: string }; meetings: Meeting[]; slots: Slot[];
  titleOf: (id: string) => string;
  onPlace: (id: string, min: number) => void;
  onUnpin: (id: string) => void;
  onResize: (id: string, minutes: number) => void;
  onMove: (id: string, dir: -1 | 1) => void;
}) {
  const grid = useRef<HTMLDivElement>(null);
  const resizing = useRef<{ id: string; y: number; minutes: number; last: number } | null>(null);
  const [hover, setHover] = useState<number | null>(null);

  const placed = slots.filter((s) => s.start !== null);
  const lo = Math.max(0, Math.min(Math.floor(toMin(workday.start) / 60) * 60, ...placed.map((s) => Math.floor(s.start! / 60) * 60)));
  const hi = Math.min(1440, Math.max(Math.ceil(toMin(workday.end) / 60) * 60, ...placed.map((s) => Math.ceil((s.start! + s.minutes) / 60) * 60)));
  const hours = Array.from({ length: Math.max(0, (hi - lo) / 60) }, (_, i) => lo + i * 60);

  type B = { key: string; s: number; e: number; meeting?: Meeting; slot?: Slot };
  const blocks: B[] = [
    ...meetings.filter((m) => !m.isAllDay).map((m, i) => ({ key: `m${i}`, s: clamp(minOf(m.start, zero), lo, hi), e: clamp(minOf(m.end, zero), lo, hi), meeting: m })).filter((b) => b.e > b.s),
    ...placed.map((p) => ({ key: p.itemId, s: p.start!, e: p.start! + p.minutes, slot: p })),
  ];
  const lane = lanes(blocks);

  const isToday = new Date().toLocaleDateString("en-CA", { timeZone: TZ }) === date;
  const nowMin = (Date.now() - zero) / 60_000;

  const minAt = (clientY: number) => {
    const r = grid.current!.getBoundingClientRect();
    const raw = lo + (clientY - r.top - (dragState.current?.grab ?? 0)) / PX;
    return clamp(Math.round(raw / SNAP) * SNAP, lo, hi - SNAP);
  };
  const fmt = (s: number, e: number) => `${toHHMM(s)}–${toHHMM(e)}`;

  return (
    <div className="flex select-none text-xs" aria-label="Day timeline" role="region">
      <div className="relative w-11 shrink-0" style={{ height: (hi - lo) * PX }} aria-hidden>
        {hours.map((h) => <span key={h} className="num muted absolute -translate-y-1/2 text-[10px]" style={{ top: (h - lo) * PX }}>{h === lo ? "" : toHHMM(h)}</span>)}
      </div>
      <div
        ref={grid}
        className="relative min-w-0 flex-1 rounded-md border border-[var(--line)]"
        style={{
          height: (hi - lo) * PX,
          backgroundImage: `repeating-linear-gradient(to bottom, var(--line) 0 1px, transparent 1px ${60 * PX}px), repeating-linear-gradient(to bottom, rgba(0,0,0,0.035) 0 1px, transparent 1px ${SNAP * PX}px)`,
        }}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          const m = minAt(e.clientY);
          if (m !== hover) setHover(m);
        }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHover(null); }}
        onDrop={(e) => {
          e.preventDefault();
          const id = e.dataTransfer.getData(DRAG_TYPE);
          if (id) onPlace(id, minAt(e.clientY));
          setHover(null);
          dragState.current = null;
        }}
      >
        {blocks.map((b) => {
          const l = lane.get(b)!;
          const top = (b.s - lo) * PX, height = Math.max(18, (b.e - b.s) * PX - 1);
          const pos = { top, height, left: `${(l.lane / l.of) * 100}%`, width: `calc(${100 / l.of}% - 3px)` };
          if (b.meeting) {
            const m = b.meeting;
            return (
              <div key={b.key} className="absolute overflow-hidden rounded-md bg-black/[0.07] px-1.5 py-0.5 text-[var(--text-2)]" style={pos} title={`${fmt(b.s, b.e)} ${m.subject}${m.location ? ` · ${m.location}` : ""}`}>
                <span className="num muted mr-1">{toHHMM(minOf(m.start, zero))}</span>
                <span className="font-medium">{m.subject}</span>
              </div>
            );
          }
          const p = b.slot!;
          const title = titleOf(p.itemId);
          return (
            <div
              key={b.key}
              role="group"
              aria-label={`${title}, ${fmt(b.s, b.e)}${p.pinned ? ", time set by you" : ""}`}
              draggable
              onDragStart={(e) => {
                if (resizing.current) { e.preventDefault(); return; }
                dragState.current = { id: p.itemId, grab: e.clientY - e.currentTarget.getBoundingClientRect().top, minutes: p.minutes };
                e.dataTransfer.setData(DRAG_TYPE, p.itemId);
                e.dataTransfer.setData("text/plain", title);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => { dragState.current = null; setHover(null); }}
              className={clsx(
                "absolute cursor-grab overflow-hidden rounded-md border bg-brand-50 text-[var(--text)] shadow-sm active:cursor-grabbing",
                p.pinned ? "border-brand-300" : "border-dashed border-brand-300",
              )}
              style={pos}
            >
              <div className="flex items-start gap-1 px-1.5 pt-0.5">
                <span className="min-w-0 flex-1 truncate font-medium leading-4">{title}</span>
                {p.pinned ? (
                  <button className="shrink-0 text-brand-700 hover:text-[var(--text)]" onClick={() => onUnpin(p.itemId)} aria-label={`Let ONE fit ${title}`} title="Time set by you. Click to let ONE fit it">
                    <Pin size={11} />
                  </button>
                ) : (
                  <span className="flex shrink-0">
                    <button className="muted hover:text-[var(--text)]" onClick={() => onMove(p.itemId, -1)} aria-label={`Earlier: ${title}`} title="Earlier"><ChevronUp size={12} /></button>
                    <button className="muted hover:text-[var(--text)]" onClick={() => onMove(p.itemId, 1)} aria-label={`Later: ${title}`} title="Later"><ChevronDown size={12} /></button>
                  </span>
                )}
              </div>
              {height >= 30 && <div className="num muted px-1.5 text-[10px] leading-4">{fmt(b.s, b.e)}</div>}
              <div
                aria-hidden
                title="Drag to change how long"
                className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize hover:bg-brand-200/60"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.setPointerCapture(e.pointerId);
                  resizing.current = { id: p.itemId, y: e.clientY, minutes: p.minutes, last: p.minutes };
                  if (!p.pinned) onPlace(p.itemId, b.s); // keep it where it is while it grows
                }}
                onPointerMove={(e) => {
                  const r = resizing.current;
                  if (!r) return;
                  const m = clamp(Math.round((r.minutes + (e.clientY - r.y) / PX) / SNAP) * SNAP, SNAP, 480);
                  if (m !== r.last) { r.last = m; onResize(r.id, m); }
                }}
                onPointerUp={() => { resizing.current = null; }}
                onPointerCancel={() => { resizing.current = null; }}
              />
            </div>
          );
        })}

        {hover !== null && (
          <div className="pointer-events-none absolute inset-x-0 rounded-md border-2 border-dashed border-brand-400 bg-brand-100/40" style={{ top: (hover - lo) * PX, height: Math.max(12, (dragState.current?.minutes ?? 30) * PX) }}>
            <span className="num absolute -top-4 left-1 rounded bg-brand-600 px-1 text-[10px] font-semibold text-white">{toHHMM(hover)}</span>
          </div>
        )}
        {isToday && nowMin > lo && nowMin < hi && (
          <div className="pointer-events-none absolute inset-x-0 border-t-2 border-red-500" style={{ top: (nowMin - lo) * PX }} aria-hidden>
            <span className="absolute -left-1 -top-[5px] size-2 rounded-full bg-red-500" />
          </div>
        )}
      </div>
    </div>
  );
}
